// client/src/pages/Campaign/CampaignMap.jsx
// Les Chroniques de Novarcane — refonte R60 « un voyage, pas un menu ».
// 1. Hub de chapitres illustrés (bannières d'actes)
// 2. Carte-journée sur fond de nation (chemin doré, nœuds SVG typés, boss médaillon)
// 3. Écran d'encounter cinématique (portrait Ken Burns, télégraphe des règles)
// 4. Événements & trésors illustrés, récompenses en cartes retournées (flip 3D)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import campaignApi from '../../api/campaignService';
import { getUserDecks } from '../../api/deckService';
import { getCardImageUrl } from '../../utils/cardUtils';
import Icon from '../../components/ui/Icon';
import {
  TYPE_META, NODE_POS, DIFF_LABELS, ALLOWED_STARS, STAR_LABELS,
  nationArt, bannerArt, duelistArt, cardBackArt,
  findNode, currentNodeId, initialStarsFor, edgePath, specialRuleLabel,
} from './campaignLogic';
import './Campaign.css';

const PENDING_KEY = 'elementum_campaign_pending';

// ─── Portrait de duelliste : art dédié si généré, sinon art de sa carte avatar ─
function Portrait({ duelist, element, className = '', alt = '' }) {
  const [src, setSrc] = useState(duelist ? duelistArt(duelist.id) : null);
  useEffect(() => {
    setSrc(duelist ? duelistArt(duelist.id) : null);
  }, [duelist && duelist.id]);
  return (
    <img
      className={className}
      src={src || undefined}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (duelist && duelist.avatar && src !== getCardImageUrl(duelist.avatar)) {
          setSrc(getCardImageUrl(duelist.avatar));
        } else {
          setSrc(null);
        }
      }}
      style={src ? undefined : { background: `rgba(var(--color-${element ? element.toLowerCase() : 'arcane'}-rgb, 98, 217, 255), 0.25)` }}
    />
  );
}

// ─── Carte récompense retournée (flip 3D) ───────────────────────────────────
function RewardCard({ card, element, delay = 0 }) {
  return (
    <div className="rw-flip" style={{ animationDelay: `${delay}ms` }}>
      <div className="rw-flip__inner">
        <div className="rw-flip__face rw-flip__back">
          <img src={cardBackArt(element)} alt="" />
        </div>
        <div className="rw-flip__face rw-flip__front">
          <img src={getCardImageUrl(card.id)} alt={card.name} loading="lazy"
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div className="rw-flip__label">
            <b>{card.name}</b>
            <span className={`rw-rarity rarity-${(card.rarity || '').toLowerCase()}`}>{card.rarity}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CampaignMap() {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [decks, setDecks] = useState([]);
  const [deckId, setDeckId] = useState('');
  const [activeAct, setActiveAct] = useState(1);
  const [selected, setSelected] = useState(null); // nœud → écran encounter/event/trésor
  const [starsChoice, setStarsChoice] = useState(2);
  const [rewards, setRewards] = useState(null); // écran récompenses
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const mapRef = useRef(null);

  const loadCampaign = useCallback(async () => {
    try {
      const data = await campaignApi.getCampaign();
      setCampaign(data.campaign);
    } catch (e) {
      setError('Impossible de charger la campagne.');
    }
  }, []);

  const loadDecks = useCallback(async () => {
    try {
      const data = await getUserDecks();
      const list = (data && (data.decks || data)) || [];
      if (Array.isArray(list) && list.length) {
        setDecks(list);
        setDeckId(list[0].id || list[0]._id);
      } else {
        setDeckId('starter_fire');
      }
    } catch {
      setDeckId('starter_fire');
    }
  }, []);

  useEffect(() => { loadCampaign(); loadDecks(); }, [loadCampaign, loadDecks]);

  // Retour de duel : valider la partie en attente → écran de victoire avec citation
  useEffect(() => {
    const pending = localStorage.getItem(PENDING_KEY);
    if (!pending) return;
    localStorage.removeItem(PENDING_KEY);
    let p;
    try { p = JSON.parse(pending); } catch { return; }
    (async () => {
      // Charger la campagne d'abord : la citation de défaite vit dans les data des nœuds
      let camp = null;
      try {
        const data = await campaignApi.getCampaign();
        camp = data.campaign;
        setCampaign(camp);
      } catch { /* la complétion rechargera */ }
      try {
        const res = await campaignApi.completeNode(p.nodeId, p.gameId);
        if (res.success && res.result === 'win') {
          const node = findNode(camp, p.nodeId);
          setRewards({
            title: 'Victoire !',
            defeatQuote: (node && node.duelist && node.duelist.defeat) || null,
            ...res.rewards,
          });
        } else if (res.success) {
          setError('Défaite… L’Éveillé plie mais ne rompt pas. Retente ta chance.');
        }
      } catch {
        // partie expirée : on ignore silencieusement
      }
      loadCampaign();
    })();
  }, []);

  const act = useMemo(
    () => campaign && campaign.acts.find((a) => a.act === activeAct),
    [campaign, activeAct]
  );

  // Nœud « courant » : le premier disponible non terminé (anneau pulsant)
  const currentId = useMemo(() => (act ? currentNodeId(act.nodes) : null), [act]);

  // ─── Edges du chemin (bezier doux, pathLength=1 pour le draw-in CSS) ─────
  const edges = useMemo(() => {
    if (!act) return [];
    const out = [];
    act.nodes.forEach((n) => {
      n.next.forEach((t, i) => {
        const target = act.nodes.find((x) => x.id === t);
        if (!target) return;
        const p1 = NODE_POS[act.nodes.indexOf(n)];
        const p2 = NODE_POS[act.nodes.indexOf(target)];
        out.push({
          key: n.id + '->' + t,
          d: edgePath(p1, p2),
          done: target.status === 'cleared',
          delay: (act.nodes.indexOf(n) * 60 + i * 30),
        });
      });
    });
    return out;
  }, [act]);

  const openNode = (node) => {
    if (node.status === 'locked') return;
    setError('');
    setSelected(node);
    setStarsChoice(initialStarsFor(node));
  };

  const closeOverlay = () => { setSelected(null); setError(''); };

  const startDuel = async () => {
    if (!selected || !selected.duelist || busy) return;
    setBusy(true);
    try {
      const res = await campaignApi.playNode(selected.id, deckId, starsChoice);
      if (res.success && res.gameId) {
        localStorage.setItem(PENDING_KEY, JSON.stringify({ nodeId: selected.id, gameId: res.gameId }));
        navigate(`/game/battle/${res.gameId}`);
      } else {
        setError('Impossible de lancer le duel.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Impossible de lancer le duel.');
    } finally {
      setBusy(false);
    }
  };

  const chooseEvent = async (choiceId) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await campaignApi.chooseEvent(selected.id, choiceId);
      if (res.success) {
        setRewards({
          title: 'Le destin tranche…',
          text: res.outcome.text,
          coins: res.outcome.coins, xp: res.outcome.xp,
          levelUps: res.outcome.levelUps, cards: [],
        });
        setSelected(null);
        loadCampaign();
      } else {
        setError(res.message || 'Choix refusé.');
      }
    } finally { setBusy(false); }
  };

  const openTreasure = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await campaignApi.openTreasure(selected.id);
      if (res.success) {
        setRewards({
          title: 'Trésor découvert !',
          coins: res.outcome.coins, xp: res.outcome.xp,
          cards: res.outcome.card ? [res.outcome.card] : [],
          levelUps: res.outcome.levelUps,
        });
        setSelected(null);
        loadCampaign();
      } else {
        setError(res.message || 'Trésor scellé.');
      }
    } finally { setBusy(false); }
  };

  if (!campaign) {
    return (
      <div className="campaign-page">
        <div className="campaign-loading">Les Chroniques s’écrivent…</div>
        {error && <div className="campaign-error">{error}</div>}
      </div>
    );
  }

  const actDone = (a) => a.nodes.filter((n) => n.status === 'cleared').length;
  const actComplete = (a) => actDone(a) === a.nodes.length;

  return (
    <div className="campaign-page">
      {/* ── Header : identité + progression ── */}
      <header className="cp-header">
        <button className="cp-back" onClick={() => navigate('/dashboard')} aria-label="Retour au cockpit">
          <Icon name="chevronLeft" size={16} /> Cockpit
        </button>
        <h1 className="cp-title">Les Chroniques de Novarcane</h1>
        <div className="cp-player">
          <span className="cp-level" title="Niveau d’Éveillé">Niv. {campaign.level}</span>
          <div className="cp-xpbar" role="progressbar" aria-valuenow={campaign.xp} aria-valuemax={campaign.xpNext}>
            <div className="cp-xpfill" style={{ width: `${Math.min(100, (campaign.xp / campaign.xpNext) * 100)}%` }} />
          </div>
          <span className="cp-stars" title="Étoiles de maîtrise"><Icon name="star" size={13} /> {campaign.totalStars}</span>
        </div>
      </header>

      {error && <div className="campaign-error">{error}</div>}

      {/* ── Hub : chapitres illustrés ── */}
      <nav className="cp-chapters" aria-label="Actes de la campagne">
        {campaign.acts.map((a) => {
          const done = actDone(a), total = a.nodes.length;
          return (
            <button
              key={a.act}
              className={`cp-chapter ${a.act === activeAct ? 'is-active' : ''} ${a.unlocked ? '' : 'is-locked'} ${actComplete(a) ? 'is-complete' : ''}`}
              style={{ ['--elem-tint']: `rgba(var(--color-${a.element.toLowerCase()}-rgb, 98, 217, 255), 1)` }}
              onClick={() => a.unlocked && setActiveAct(a.act)}
              disabled={!a.unlocked}
              title={a.unlocked ? a.title : 'Termine l’acte précédent pour briser le sceau'}
            >
              <img className="cp-chapter__art" src={bannerArt(a.act)} alt="" loading="lazy" />
              <span className="cp-chapter__veil" />
              {!a.unlocked && <span className="cp-chapter__lock"><Icon name="lock" size={16} /></span>}
              <span className="cp-chapter__label">
                <b>{a.nation}</b>
                <i>Acte {a.act}{a.unlocked && !actComplete(a) ? ` · ${done}/${total}` : ''}</i>
              </span>
              {actComplete(a) && <span className="cp-chapter__done"><Icon name="star" size={14} /></span>}
            </button>
          );
        })}
      </nav>

      {/* ── Scène d'acte : carte-journée sur fond de nation ── */}
      {act && (
        <section className="cp-act" key={act.act} style={{ ['--elem-tint']: `rgba(var(--color-${act.element.toLowerCase()}-rgb, 98, 217, 255), 0.55)` }}>
          <div className="cp-act__head">
            <div className="cp-act__band">
              <img src={bannerArt(act.act)} alt="" loading="lazy" />
            </div>
            <p className="cp-act__kicker">~ Acte {act.act} · {act.nation} ~</p>
            <h2 className="cp-act__title">{act.title}</h2>
            <p className="cp-act__intro">{act.intro}</p>
          </div>

          <div className="cp-map" ref={mapRef}>
            <img className="cp-map__bg" src={nationArt(act.element)} alt="" />
            <div className="cp-map__shade" />
            <svg className="cp-map__svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {edges.map((e) => (
                <path key={e.key} className={`cp-edge ${e.done ? 'is-done' : ''}`} d={e.d}
                      pathLength="1" style={{ animationDelay: `${e.delay}ms` }} />
              ))}
            </svg>
            {act.nodes.map((n, i) => {
              const pos = NODE_POS[i];
              const t = TYPE_META[n.type];
              const isBoss = n.type === 'BOSS';
              const isCurrent = n.id === currentId && n.status === 'available';
              return (
                <button
                  key={n.id}
                  className={`cp-node cp-node--${n.type.toLowerCase()} cp-node--${n.status} ${isCurrent ? 'is-current' : ''} ${isBoss ? 'cp-node--boss' : ''}`}
                  style={{ left: `${pos.x}%`, top: `${pos.y}%`, animationDelay: `${120 + i * 55}ms` }}
                  onClick={() => openNode(n)}
                  aria-label={`${t.label}${n.name && n.type !== 'EVENT' && n.type !== 'TREASURE' ? ' — ' + n.name.replace(/^(Élite|BOSS) — /, '') : ''}`}
                >
                  {isBoss && n.status !== 'locked' ? (
                    <span className="cp-node__medal">
                      <Portrait duelist={n.duelist} element={act.element} className="cp-node__portrait" alt={n.name} />
                      <Icon name="crown" size={18} className="cp-node__crown" />
                    </span>
                  ) : (
                    <span className="cp-node__ring">
                      {n.status === 'locked' ? <Icon name="lock" size={17} /> : <Icon name={t.icon} size={isBoss ? 22 : 18} />}
                    </span>
                  )}
                  {n.status === 'cleared' && (n.type === 'EVENT' || n.type === 'TREASURE') && (
                    <span className="cp-node__check"><Icon name="sparkles" size={12} /></span>
                  )}
                  {n.status === 'cleared' && n.type !== 'EVENT' && n.type !== 'TREASURE' && (
                    <span className="cp-node__stars">{'★'.repeat(n.stars)}{'☆'.repeat(Math.max(0, 3 - n.stars))}</span>
                  )}
                  {isCurrent && <span className="cp-node__halo" />}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Écran d'encounter / événement / trésor ── */}
      {selected && (
        <div className="cine" onClick={closeOverlay} role="dialog" aria-modal="true">
          <div className="cine__panel" onClick={(e) => e.stopPropagation()}
               style={{ ['--elem-tint']: act ? `rgba(var(--color-${act.element.toLowerCase()}-rgb, 98, 217, 255), 0.8)` : undefined }}>

            {selected.duelist ? (
              <>
                <div className="cine__art">
                  <Portrait duelist={selected.duelist} element={act && act.element} className="cine__portrait kenburns" alt={selected.duelist.name} />
                  <div className="cine__artshade" />
                </div>
                <div className="cine__body">
                  <p className="cine__eyebrow">Acte {act && act.act} · {TYPE_META[selected.type].eyebrow}</p>
                  <h2 className="cine__name">{selected.duelist.name}</h2>
                  <p className="cine__rank">{selected.duelist.title}</p>
                  <blockquote className="cine__quote">« {selected.duelist.intro.replace(/^«\s*/, '').replace(/\s*»$/, '')} »</blockquote>

                  <ul className="cine__telegraph" aria-label="Règles de l’encounter">
                    <li className="cp-chip cp-chip--diff"><Icon name="status" size={13} /> {DIFF_LABELS[selected.duelist.difficulty] || selected.duelist.difficulty}</li>
                    {(selected.duelist.specialRules || []).map((r, i) => (
                      <li key={i} className="cp-chip"><Icon name="sparkles" size={13} /> {specialRuleLabel(r)}</li>
                    ))}
                  </ul>

                  <div className="cine__setup">
                    <label className="cine__deck">
                      <span>Ton deck</span>
                      <select value={deckId} onChange={(e) => setDeckId(e.target.value)}>
                        {decks.length > 0
                          ? decks.map((d) => <option key={d.id || d._id} value={d.id || d._id}>{d.name || d.id}</option>)
                          : <option value="starter_fire">Deck de départ</option>}
                      </select>
                    </label>
                    <div className="cine__stars" role="radiogroup" aria-label="Difficulté choisie">
                      {(ALLOWED_STARS[selected.duelist.difficulty] || [2, 3]).map((s) => (
                        <button key={s} className={`cine__starbtn ${starsChoice === s ? 'is-on' : ''}`} onClick={() => setStarsChoice(s)}
                                role="radio" aria-checked={starsChoice === s}>
                          <span className="cine__starbtn__stars">{'★'.repeat(s)}{'☆'.repeat(3 - s)}</span>
                          <span className="cine__starbtn__label">{STAR_LABELS[s]}</span>
                        </button>
                      ))}
                    </div>
                    {selected.status === 'cleared' && (
                      <p className="cine__replay"><Icon name="coins" size={13} /> Déjà vaincu — rejeu : 50 or max/jour, améliore tes étoiles.</p>
                    )}
                    <button className="cine__cta" onClick={startDuel} disabled={busy}>
                      <Icon name="swords" size={16} /> {busy ? 'Invocation du duel…' : 'En garde !'}
                    </button>
                  </div>
                </div>
              </>
            ) : selected.type === 'EVENT' ? (
              <div className="cine__body cine__body--event">
                <p className="cine__eyebrow">Acte {act && act.act} · Rencontre</p>
                <div className="cine__eventicon"><Icon name="scroll" size={26} /></div>
                <p className="cine__eventtext">{selected.event && selected.event.text}</p>
                {selected.event && selected.event.chosen ? (
                  <p className="cine__eventchosen"><Icon name="sparkles" size={14} /> Le destin a déjà tranché ici. La route continue.</p>
                ) : (
                  <div className="cine__choices">
                    {(selected.event && selected.event.choices || []).map((c) => (
                      <button key={c.id} className="cine__choice" onClick={() => chooseEvent(c.id)} disabled={busy}>
                        <Icon name="sparkles" size={15} />
                        <span>{c.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="cine__body cine__body--treasure">
                <p className="cine__eyebrow">Acte {act && act.act} · Trésor</p>
                <div className="cine__chest"><Icon name="gem" size={34} /></div>
                <p className="cine__eventtext">Un coffre scellé d’Æther pulse doucement. Une seule âme peut l’ouvrir — la tienne.</p>
                {selected.status === 'cleared'
                  ? <p className="cine__eventchosen"><Icon name="sparkles" size={14} /> Trésor déjà ouvert. L’écho de l’or reste.</p>
                  : <button className="cine__cta" onClick={openTreasure} disabled={busy}><Icon name="gem" size={16} /> Ouvrir le trésor</button>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Écran de récompenses : flip 3D ── */}
      {rewards && (
        <div className="cine cine--rewards" onClick={() => setRewards(null)} role="dialog" aria-modal="true">
          <div className="cine__panel cine__panel--rewards" onClick={(e) => e.stopPropagation()}
               style={{ ['--elem-tint']: act ? `rgba(var(--color-${act.element.toLowerCase()}-rgb, 98, 217, 255), 0.8)` : undefined }}>
            <h2 className="rw-title">{rewards.title}</h2>
            {rewards.defeatQuote && (
              <blockquote className="rw-quote">« {rewards.defeatQuote.replace(/^«\s*/, '').replace(/\s*»$/, '')} »</blockquote>
            )}
            {rewards.text && <p className="rw-text">{rewards.text}</p>}

            {(rewards.cards || []).length > 0 && (
              <div className="rw-flips">
                {rewards.cards.map((c, i) => <RewardCard key={c.id} card={c} element={act ? act.element : 'neutral'} delay={450 + i * 260} />)}
              </div>
            )}

            <ul className="rw-list">
              {rewards.coins ? <li><Icon name="coins" size={15} /> {rewards.coins} pièces d’or</li> : null}
              {rewards.xp ? <li><Icon name="sparkles" size={15} /> {rewards.xp} XP</li> : null}
              {rewards.stars ? <li><Icon name="star" size={15} /> {'★'.repeat(rewards.stars)}{'☆'.repeat(3 - rewards.stars)} maîtrise</li> : null}
              {rewards.unlockedCardBack && (
                <li><Icon name="cards" size={15} /> Dos de carte débloqué : <b>{rewards.unlockedCardBack.name}</b></li>
              )}
              {(rewards.levelUps || []).map((l) => (
                <li key={l.level} className="rw-levelup"><Icon name="status" size={15} /> Niveau {l.level} atteint — +{l.coins} or{l.title ? ` · Titre : « ${l.title} »` : ''}</li>
              ))}
            </ul>

            <button className="cine__cta" onClick={() => setRewards(null)}>Continuer la route</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Le composant est le seul export JSX (tests logiques → campaignLogic.js)
export default CampaignMap;
